import { isBefore, parseISO } from 'date-fns'

import { EMPTY_LIST } from '../../../constants/common'
import { DATE_SHORT_FORMAT } from '../../../constants/date'
import { HTML_LINK_TARGET } from '../../../constants/html'
import {
  RENTAL_PORTAL_FACT_SEPARATOR,
  RENTAL_PORTAL_VIEWING_STATUS,
  RENTAL_PORTAL_VIEWING_STATUSES,
  VIEWING_TERMINAL_STATUSES,
} from '../../../constants/rentalPortal'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import {
  getIsViewingPast,
  getViewingObjectName,
  getViewingTimeRangeLabel,
} from '../../RentalPortalViewing'
import { formatDate } from '../../Table'

import { QA_NAME_VIEWING_PROFILE_PREVIEW_OPEN_FULL_PAGE } from './RentalPortalViewingProfile.constants'

function getIsViewingCancelled(viewingData) {
  return viewingData.status === RENTAL_PORTAL_VIEWING_STATUS.cancelled
}

function getViewingStatusTransitions(viewingData) {
  if (!viewingData || getIsViewingTerminal(viewingData)) {
    return EMPTY_LIST
  }

  return getHasViewingStarted(viewingData)
    ? [
        RENTAL_PORTAL_VIEWING_STATUS.completed,
        RENTAL_PORTAL_VIEWING_STATUS.noShow,
      ]
    : [RENTAL_PORTAL_VIEWING_STATUS.cancelled]
}

function toViewingStatusSelectOption({ status, t }) {
  return {
    label: t(RENTAL_PORTAL_VIEWING_STATUSES[status].text),
    textColor: RENTAL_PORTAL_VIEWING_STATUSES[status].textColor,
    value: status,
  }
}

export function getViewingProfileTitle({ locale, t, timezone, viewingData }) {
  return viewingData
    ? getViewingObjectName({ locale, t, timezone, viewing: viewingData })
    : null
}

export function getViewingProfileTimeLabel({ locale, timezone, viewingData }) {
  return viewingData
    ? `${formatDate({
        date: viewingData.start,
        dateFormat: DATE_SHORT_FORMAT,
        locale,
        timezone,
        useTodayFormat: false,
      })}${RENTAL_PORTAL_FACT_SEPARATOR}${getViewingTimeRangeLabel({
        end: viewingData.end,
        locale,
        start: viewingData.start,
        timezone,
      })}`
    : null
}

export function toViewingProfileApplicantValue({ applicant, label }) {
  return applicant
    ? { avatarColor: applicant.avatar_color, fullname: label }
    : null
}

export function toViewingProfileManagerValue(viewingData) {
  return viewingData?.manager_label
    ? {
        avatarColor: viewingData.manager_avatar_color,
        fullname: viewingData.manager_label,
      }
    : null
}

export function getViewingProfilePreviewOpenFullPageProps({
  Actions,
  t,
  viewingId,
  onClosePreview,
}) {
  function getProfileLink({ id }) {
    return PROFILE_ROUTE_NAMES.rentalPortalViewingProfile({ id })
  }

  return {
    Actions,
    getProfileLink,
    id: viewingId,
    openInNewTabButtonProps: {
      qaName: QA_NAME_VIEWING_PROFILE_PREVIEW_OPEN_FULL_PAGE,
      tooltip: t('RentalPortal.ListingProfilePreviewOpenFullPageBtnKey'),
    },
    openLinkTarget: HTML_LINK_TARGET.self,
    onClosePreview,
  }
}

export function getCanEditViewingNote(viewingData) {
  return viewingData?.permissions?.can_edit_content
}

export function getIsViewingTerminal(viewingData) {
  return VIEWING_TERMINAL_STATUSES.includes(viewingData?.status)
}

export function getCanRescheduleViewing(viewingData) {
  return (
    viewingData &&
    (!getIsViewingTerminal(viewingData) || getIsViewingCancelled(viewingData))
  )
}

export function getHasViewingStarted(viewingData) {
  return viewingData && isBefore(parseISO(viewingData.start), new Date())
}

export function getViewingStatusSelectOptions({ t, viewingData }) {
  return getViewingStatusTransitions(viewingData).map(status =>
    toViewingStatusSelectOption({ status, t })
  )
}

export function getViewingStatusSelectValue({ status, t }) {
  return RENTAL_PORTAL_VIEWING_STATUSES[status]
    ? toViewingStatusSelectOption({ status, t })
    : null
}

export function getCanEditViewingStatus(viewingData) {
  return viewingData?.permissions?.can_edit_status
}

export function getIsViewingCalendarExportable(viewingData) {
  return (
    viewingData?.start && viewingData.end && !getIsViewingCancelled(viewingData)
  )
}

export function getViewingProfileData({ isPreview, timezone, viewingData }) {
  return isPreview &&
    viewingData &&
    getIsViewingPast({ timezone, viewing: viewingData })
    ? {
        ...viewingData,
        permissions: {
          ...viewingData.permissions,
          can_edit_content: false,
          can_edit_status: false,
          can_manage: false,
        },
      }
    : viewingData
}
